import { RefObject, useEffect, useState } from "react";

export function useStickyColumnOffsets(
  frozenKeysInOrder: string[],
  headerRefs: RefObject<Record<string, HTMLTableCellElement | null>>,
  tableRef: RefObject<HTMLDivElement | null>,
  dependencyKey = "",
  leadingOffsetKey?: string,
) {
  const [stickyLeft, setStickyLeft] = useState<Record<string, number>>({});

  useEffect(() => {
    const updateOffsets = () => {
      let cumulativeLeft = leadingOffsetKey
        ? headerRefs.current?.[leadingOffsetKey]?.offsetWidth ?? 0
        : 0;
      const next: Record<string, number> = {};

      frozenKeysInOrder.forEach((key) => {
        next[key] = cumulativeLeft;
        const width = headerRefs.current?.[key]?.offsetWidth ?? 0;
        cumulativeLeft += width;
      });

      setStickyLeft((prev) => {
        const unchanged =
          Object.keys(next).length === Object.keys(prev).length &&
          frozenKeysInOrder.every((key) => prev[key] === next[key]);

        return unchanged ? prev : next;
      });
    };

    const container = tableRef.current;
    const resizeObserver = new ResizeObserver(updateOffsets);

    if (container) {
      resizeObserver.observe(container);
    }

    updateOffsets();

    return () => {
      resizeObserver.disconnect();
    };
  }, [frozenKeysInOrder, headerRefs, tableRef, dependencyKey, leadingOffsetKey]);

  return stickyLeft;
}

export function getFrozenCellSx(
  key: string,
  frozenKeysInOrder: string[],
  stickyLeft: Record<string, number>,
  isHeader: boolean,
) {
  const frozenIndex = frozenKeysInOrder.indexOf(key);
  if (frozenIndex === -1) {
    return {};
  }

  return {
    position: "sticky" as const,
    left: stickyLeft[key] ?? 0,
    zIndex: isHeader ? 110 - frozenIndex : 100 - frozenIndex,
  };
}
